/**
 * @file DateRangePopover.tsx
 * @brief Desktop date-range picker: presets on the left, two months, Clear / Filter.
 *
 * @details
 * DES (30-09-2026), from a reference picker: "range picker bhi aisay hi ayee,
 * its better for web and desktop, but in our colour and style like rounded
 * corners". It opens right under its anchor (no dimmed page, no side sheet):
 * - **Left:** the caller's presets, as pills; the one in use is solid brand.
 *   Clicking one only selects it (the calendars show it); Filter applies.
 * - **Right:** two consecutive months, each with month and year dropdowns,
 *   arrows on the outer edges. First click sets the start, second the end,
 *   with a soft band previewing the range under the pointer.
 * - **Footer:** the range in words, Clear (outline pill), Filter (brand pill).
 * - Below `lg` it is centred on the screen with one month (the range's
 *   end), and the presets become a chip row.
 *
 * Closes on Escape and on a click outside it and its anchor. Weeks start on
 * Monday, like `CalendarGrid`.
 *
 * `DatePopover` (01-10-2026) is the one-date sibling on the same surface:
 * forward-looking presets, one month, Clear / Apply. `DatePicker` opens it.
 *
 * @author DES
 * @date 30-09-2026
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/shared/theme/cn";

export type DateRangePresetDef = {
  id: string;
  label: string;
  range: () => { from: Date; to: Date };
};

export type DateRangeValue = {
  /** A preset id, or null for a range picked on the calendars. */
  presetId: string | null;
  from: Date | null;
  to: Date | null;
};

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = Array.from({ length: 12 }, (_, i) => format(new Date(2026, i, 1), "MMMM"));

function weeksOf(month: Date): Date[][] {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  return Array.from({ length: 6 }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)),
  );
}

// ----------------------------------------------------------------------
// Month / year dropdown: a round pill over a native select
// ----------------------------------------------------------------------
function PillSelect({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: number;
  options: { value: number; label: string; disabled?: boolean }[];
  onChange: (v: number) => void;
  label: string;
  className?: string;
}) {
  return (
    <span className={cn("relative inline-flex", className)}>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-10 w-full cursor-pointer appearance-none rounded-full border border-input bg-field pl-4 pr-9 text-sm font-semibold text-foreground outline-none transition-colors hover:bg-secondary focus:ring-2 focus:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    </span>
  );
}

// ----------------------------------------------------------------------
// One month
// ----------------------------------------------------------------------
function Month({
  month,
  onMonthChange,
  onMonthPick,
  prev,
  next,
  from,
  to,
  hover,
  onDay,
  onHover,
  minDate = null,
}: {
  month: Date;
  onMonthChange: (m: Date) => void;
  /** The month / year dropdowns (they pick; the arrows only move). */
  onMonthPick: (m: Date) => void;
  /** "narrow": the arrow shows only below `lg`, where this month is alone. */
  prev: boolean | "narrow";
  next: boolean;
  from: Date | null;
  to: Date | null;
  hover: Date | null;
  onDay: (d: Date) => void;
  onHover: (d: Date | null) => void;
  /** Days before this one are greyed out and can't be picked. */
  minDate?: Date | null;
}) {
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 8 }, (_, i) => thisYear - 6 + i).map((y) => ({
    value: y,
    label: String(y),
  }));
  // While the end is being picked, the band follows the pointer.
  const end = to ?? (from && hover && !isBefore(hover, from) ? hover : null);

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-3 flex items-center gap-2">
        {prev ? (
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => onMonthChange(addMonths(month, -1))}
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-secondary",
              prev === "narrow" && "lg:hidden",
            )}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : null}
        <PillSelect
          label="Month"
          className="min-w-0 flex-[3] lg:min-w-[8.5rem]"
          value={month.getMonth()}
          options={MONTHS.map((m, i) => ({ value: i, label: m }))}
          onChange={(m) => onMonthPick(new Date(month.getFullYear(), m, 1))}
        />
        <PillSelect
          label="Year"
          className="min-w-[5.75rem] flex-[2]"
          value={month.getFullYear()}
          options={years}
          onChange={(y) => onMonthPick(new Date(y, month.getMonth(), 1))}
        />
        {next ? (
          <button
            type="button"
            aria-label="Next month"
            onClick={() => onMonthChange(addMonths(month, 1))}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-secondary"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-7 text-center text-xs font-semibold text-muted-foreground">
        {WEEKDAYS.map((d) => (
          <span key={d} className="pb-2">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7" onMouseLeave={() => onHover(null)}>
        {weeksOf(month)
          .flat()
          .map((day) => {
            const inMonth = isSameMonth(day, month);
            if (!inMonth) return <span key={day.toISOString()} className="h-10" />;
            const past = !!minDate && isBefore(day, startOfDay(minDate));
            const isStart = !!from && isSameDay(day, from);
            const isEnd = !!end && isSameDay(day, end);
            const between = !!from && !!end && isAfter(day, from) && isBefore(day, startOfDay(end));
            const inBand = (isStart || isEnd || between) && !!end && !isSameDay(from!, end);
            return (
              <span
                key={day.toISOString()}
                className={cn(
                  "relative flex h-10 items-center justify-center",
                  inBand && "bg-brand/10",
                  // Round the band where a week starts or ends, so each row reads as one pill.
                  inBand &&
                    (isStart || day.getDay() === 1 || day.getDate() === 1) &&
                    "rounded-l-full",
                  inBand &&
                    (isEnd || day.getDay() === 0 || isSameDay(day, endOfMonth(day))) &&
                    "rounded-r-full",
                )}
              >
                <button
                  type="button"
                  disabled={past}
                  onClick={() => onDay(startOfDay(day))}
                  onMouseEnter={() => onHover(startOfDay(day))}
                  className={cn(
                    "relative flex h-10 w-10 items-center justify-center rounded-full text-sm tabular-nums transition-colors",
                    isStart || isEnd
                      ? "bg-brand font-bold text-white"
                      : "text-foreground hover:bg-secondary",
                    !isStart && !isEnd && isToday(day) && "font-bold text-brand",
                    past && "cursor-not-allowed text-muted-foreground/40 hover:bg-transparent",
                  )}
                >
                  {format(day, "d")}
                </button>
              </span>
            );
          })}
      </div>
    </div>
  );
}

/** Start's month on the left, end's on the right; one-month ranges end on the right. */
function monthsFor(from: Date | null, to: Date | null) {
  const right = startOfMonth(to ?? from ?? new Date());
  const fromMonth = startOfMonth(from ?? right);
  return {
    left: isBefore(fromMonth, right) ? fromMonth : addMonths(right, -1),
    right,
  };
}

// ----------------------------------------------------------------------
// Shared panel mechanics: placed under its anchor (desktop) in a portal,
// centred below `lg`; closes on Escape and on a press outside both.
// ----------------------------------------------------------------------
function useAnchoredPanel(open: boolean, onOpenChange: (open: boolean) => void) {
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Desktop: placed under the anchor in page coordinates, in a portal, so a
  // card's `overflow-hidden` never clips it and an anchor on the left does
  // not push it off screen (30-09-2026, when every range picker moved here).
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      if (!window.matchMedia("(min-width: 1024px)").matches) {
        setPos(null);
        return;
      }
      const a = wrapRef.current?.getBoundingClientRect();
      const panel = panelRef.current;
      if (!a) return;
      const w = panel?.offsetWidth ?? 600;
      const h = panel?.offsetHeight ?? 480;
      const left = Math.min(Math.max(16, a.right - w), window.innerWidth - w - 16);
      const below = a.bottom + 12;
      const top =
        below + h <= window.innerHeight - 16
          ? below
          : Math.max(16, a.top - 12 - h >= 16 ? a.top - 12 - h : window.innerHeight - h - 16);
      setPos({ top, left });
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return { wrapRef, panelRef, pos };
}

// ----------------------------------------------------------------------
// The popover
// ----------------------------------------------------------------------
export function DateRangePopover({
  open,
  onOpenChange,
  presets,
  value,
  onApply,
  onClear,
  anchor,
  anchorClassName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: DateRangePresetDef[];
  value: DateRangeValue;
  onApply: (next: { presetId: string | null; from: Date; to: Date }) => void;
  /** Clear: the caller's default (the Boardrooms list goes back to This month). */
  onClear: () => void;
  /** The button it opens under; its right edge is the popover's right edge. */
  anchor: ReactNode;
  /** Classes for the anchor's wrapper (e.g. `flex w-full` for a full-width
   *  filter field). Default `inline-flex`. */
  anchorClassName?: string;
}) {
  const { wrapRef, panelRef, pos } = useAnchoredPanel(open, onOpenChange);
  const [draft, setDraft] = useState<DateRangeValue>(value);
  const [hover, setHover] = useState<Date | null>(null);
  // The two months move independently (DES 30-09-2026: picking Aug 2025 on
  // the left and Aug 2026 on the right pulled the left one to 2026, because
  // the right was always "left + 1"). Left = the start's month, right = the
  // end's. They only push each other when they would cross.
  const [months, setMonths] = useState(() => monthsFor(value.from, value.to));
  const leftAt = (m: Date) => {
    const left = startOfMonth(m);
    return { left, right: isBefore(left, months.right) ? months.right : addMonths(left, 1) };
  };
  const rightAt = (m: Date) => {
    const right = startOfMonth(m);
    return { left: isAfter(right, months.left) ? months.left : addMonths(right, -1), right };
  };
  // Arrows only move the view.
  const setLeftMonth = (m: Date) => setMonths(leftAt(m));
  const setRightMonth = (m: Date) => setMonths(rightAt(m));
  // The month / year dropdowns also PICK (DES 30-09-2026: "when i selected
  // custom date why its stuck on this month"): the left one starts the range
  // on the 1st of its month, the right one ends it on its month's last day,
  // and the range becomes Custom. While a start is clicked and the end is
  // still to come, they only move the view, so the end day can be clicked.
  const midPick = !!draft.from && !draft.to;
  const pickLeftMonth = (m: Date) => {
    const next = leftAt(m);
    setMonths(next);
    if (midPick) return;
    const from = next.left;
    setDraft((cur) => ({
      presetId: null,
      from,
      to: cur.to && isAfter(cur.to, from) ? cur.to : endOfMonth(next.right),
    }));
  };
  const pickRightMonth = (m: Date) => {
    const next = rightAt(m);
    setMonths(next);
    if (midPick) return;
    const to = endOfDay(endOfMonth(next.right));
    setDraft((cur) => ({
      presetId: null,
      from: cur.from && isBefore(cur.from, to) ? cur.from : next.left,
      to,
    }));
  };

  // Fresh draft each time it opens.
  useEffect(() => {
    if (!open) return;
    setDraft(value);
    setHover(null);
    setMonths(monthsFor(value.from, value.to));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickDay = (d: Date) => {
    setDraft((cur) => {
      if (!cur.from || cur.to || isBefore(d, cur.from)) {
        return { presetId: null, from: d, to: null };
      }
      return { presetId: null, from: cur.from, to: endOfDay(d) };
    });
  };

  const pickPreset = (p: DateRangePresetDef) => {
    const r = p.range();
    setDraft({ presetId: p.id, from: r.from, to: r.to });
    setMonths(monthsFor(r.from, r.to));
  };

  const canApply = !!draft.from && !!draft.to;
  const summary = useMemo(() => {
    if (!draft.from) return "Custom: click a start date, then an end date";
    if (!draft.to) return `${format(draft.from, "d MMM yyyy")} – pick an end date`;
    return `${format(draft.from, "d MMM yyyy")} – ${format(draft.to, "d MMM yyyy")}`;
  }, [draft]);

  const presetButton = (p: DateRangePresetDef, chip: boolean) => {
    const on = draft.presetId === p.id;
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => pickPreset(p)}
        className={cn(
          "shrink-0 rounded-full text-left text-[15px] font-semibold transition-colors",
          chip ? "h-9 px-4" : "h-11 w-full px-4",
          on ? "bg-brand text-white" : "text-foreground hover:bg-secondary",
          chip && !on && "bg-secondary",
        )}
      >
        {p.label}
      </button>
    );
  };
  // Custom is on whenever no preset is: after "Custom" is clicked (an empty
  // pick, waiting for a start) or once days are picked by hand.
  const customOn = draft.presetId === null;
  // DES 30-09-2026: "custom kiyoon nahin chal raha". It was a label that only
  // lit up after picking days; now it is a button that clears the selection
  // and asks for a start date, so the next two clicks are the range.
  const startCustom = () => {
    setDraft({ presetId: null, from: null, to: null });
    setHover(null);
  };
  const customButton = (chip: boolean) => (
    <button
      type="button"
      onClick={startCustom}
      className={cn(
        "shrink-0 rounded-full text-left text-[15px] font-semibold transition-colors",
        chip ? "h-9 px-4" : "h-11 w-full px-4",
        customOn ? "bg-brand text-white" : "text-foreground hover:bg-secondary",
        chip && !customOn && "bg-secondary",
      )}
    >
      Custom
    </button>
  );

  return (
    <span ref={wrapRef} className={cn("relative", anchorClassName ?? "inline-flex")}>
      {anchor}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Select date range"
              data-vz-popover=""
              // The "⋯" menu's surface: popover fill, 1px border, shadow-md
              // (DES 30-09-2026: "bg shared image walay jaisa hi ho").
              style={pos ? { top: pos.top, left: pos.left } : undefined}
              className={cn(
                "pointer-events-auto fixed z-[60] max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden rounded-[28px] border bg-popover text-left text-popover-foreground shadow-md lg:w-[52rem] lg:overflow-hidden",
                pos ? "" : "inset-x-4 top-1/2 -translate-y-1/2 lg:invisible",
              )}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Presets: a column on desktop, a chip row when narrow. */}
                <div className="hidden w-44 shrink-0 flex-col gap-1 border-r border-border p-3 lg:flex">
                  {presets.map((p) => presetButton(p, false))}
                  {customButton(false)}
                </div>
                <div className="flex gap-2 overflow-x-auto border-b border-border p-3 [scrollbar-width:none] lg:hidden">
                  {presets.map((p) => presetButton(p, true))}
                  {customButton(true)}
                </div>

                {/* Two months on desktop; below `lg` only the right-hand one, so
                the month in use (the range's end) is the one shown. */}
                <div className="flex min-w-0 flex-1 gap-6 p-4">
                  <div className="hidden min-w-0 flex-1 lg:block">
                    <Month
                      month={months.left}
                      onMonthChange={setLeftMonth}
                      onMonthPick={pickLeftMonth}
                      prev
                      next={false}
                      from={draft.from}
                      to={draft.to}
                      hover={hover}
                      onDay={pickDay}
                      onHover={setHover}
                    />
                  </div>
                  <Month
                    month={months.right}
                    onMonthChange={setRightMonth}
                    onMonthPick={pickRightMonth}
                    prev="narrow"
                    next
                    from={draft.from}
                    to={draft.to}
                    hover={hover}
                    onDay={pickDay}
                    onHover={setHover}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border px-4 py-3">
                <span className="w-full text-sm tabular-nums text-muted-foreground lg:mr-auto lg:w-auto">
                  {summary}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onClear();
                    onOpenChange(false);
                  }}
                  className="h-10 rounded-full border border-brand px-6 text-sm font-semibold text-brand transition-colors hover:bg-brand/5"
                >
                  Clear
                </button>
                <button
                  type="button"
                  disabled={!canApply}
                  onClick={() => {
                    if (!draft.from || !draft.to) return;
                    onApply({ presetId: draft.presetId, from: draft.from, to: draft.to });
                    onOpenChange(false);
                  }}
                  className="h-10 rounded-full bg-brand px-7 text-sm font-semibold text-white transition-colors hover:bg-brand/90 disabled:opacity-50"
                >
                  Filter
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

// ----------------------------------------------------------------------
// One date: the same popover, one month (DES 01-10-2026, on the to-do's
// Due date side sheet: "yeh bhi range picker type karo")
// ----------------------------------------------------------------------
export type DatePresetDef = { id: string; label: string; date: () => Date };

const pad2 = (n: number) => String(n).padStart(2, "0");
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: h, label: pad2(h) }));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, m) => ({ value: m, label: pad2(m) }));

/** Forward-looking presets: due dates and bookings. */
const UPCOMING_DATE_PRESETS: DatePresetDef[] = [
  { id: "today", label: "Today", date: () => startOfDay(new Date()) },
  { id: "tomorrow", label: "Tomorrow", date: () => startOfDay(addDays(new Date(), 1)) },
  {
    id: "next-monday",
    label: "Next Monday",
    date: () => startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 }),
  },
  { id: "in-a-week", label: "In a week", date: () => startOfDay(addDays(new Date(), 7)) },
  { id: "in-2-weeks", label: "In 2 weeks", date: () => startOfDay(addDays(new Date(), 14)) },
  { id: "in-a-month", label: "In a month", date: () => startOfDay(addMonths(new Date(), 1)) },
];

export function DatePopover({
  open,
  onOpenChange,
  value,
  onApply,
  onClear,
  anchor,
  anchorClassName,
  presets = UPCOMING_DATE_PRESETS,
  title,
  withTime = false,
  disablePast = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: Date | null;
  onApply: (date: Date) => void;
  /** Shown only while a date is held. */
  onClear?: () => void;
  anchor: ReactNode;
  anchorClassName?: string;
  presets?: DatePresetDef[];
  /** Read out as the panel's name, e.g. "Due date". */
  title?: string;
  /** Adds Hour and Minute pills under the month (DES 01-10-2026, Schedule
   *  for later: "we need to use new picker, just add time to it"). The
   *  applied Date then carries that time instead of midnight. */
  withTime?: boolean;
  /** No past days, and with `withTime` no past hours or minutes today
   *  (DES 01-10-2026, Schedule for later: "past times disable karo, aaj ke
   *  guzray huay hours bhi"). Presets before today are greyed out. */
  disablePast?: boolean;
}) {
  const { wrapRef, panelRef, pos } = useAnchoredPanel(open, onOpenChange);
  const [draft, setDraft] = useState<Date | null>(value);
  const [month, setMonth] = useState(() => startOfMonth(value ?? new Date()));
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);

  // Fresh draft each time it opens. With no time held yet, start on the
  // next whole hour: these are bookings, so a future time.
  useEffect(() => {
    if (!open) return;
    setDraft(value ? startOfDay(value) : null);
    setMonth(startOfMonth(value ?? new Date()));
    const start = value ?? new Date(Date.now() + 60 * 60 * 1000);
    setHour(start.getHours());
    setMinute(value ? value.getMinutes() : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const withClock = (d: Date, h = hour, m = minute) => {
    if (!withTime) return d;
    const next = new Date(d);
    next.setHours(h, m, 0, 0);
    return next;
  };

  // Read once per render: "now" for the past checks below.
  const now = new Date();
  const minDate = disablePast ? now : null;
  // On today, the hours and minutes already gone are disabled.
  const onToday = disablePast && !!draft && isSameDay(draft, now);
  const hourOptions = HOUR_OPTIONS.map((o) => ({
    ...o,
    disabled: onToday && o.value < now.getHours(),
  }));
  const minuteOptions = MINUTE_OPTIONS.map((o) => ({
    ...o,
    disabled: onToday && hour === now.getHours() && o.value <= now.getMinutes(),
  }));
  const draftPast = disablePast && !!draft && withClock(draft).getTime() <= now.getTime();

  /** The first whole 5 minutes after now: where a past time on today moves. */
  const nextSlot = () => {
    const t = new Date(Date.now() + 60 * 1000);
    t.setSeconds(0, 0);
    t.setMinutes(Math.ceil(t.getMinutes() / 5) * 5);
    return t;
  };
  // Picking today with a time already gone moves the time to the next slot.
  const bumpIfPast = (day: Date, h = hour, m = minute) => {
    if (!disablePast || !withTime) return;
    if (withClock(day, h, m).getTime() > Date.now()) return;
    const t = nextSlot();
    if (!isSameDay(t, day)) return; // late tonight: nothing left today
    setHour(t.getHours());
    setMinute(t.getMinutes());
  };

  const pick = (d: Date) => {
    if (disablePast && isBefore(d, startOfDay(new Date()))) return;
    setDraft(startOfDay(d));
    setMonth(startOfMonth(d));
    bumpIfPast(startOfDay(d));
  };

  const presetButton = (p: DatePresetDef, chip: boolean) => {
    const on = !!draft && isSameDay(draft, p.date());
    const gone = disablePast && isBefore(p.date(), startOfDay(new Date()));
    return (
      <button
        key={p.id}
        type="button"
        disabled={gone}
        onClick={() => pick(p.date())}
        className={cn(
          "shrink-0 rounded-full text-left text-[15px] font-semibold transition-colors",
          chip ? "h-9 px-4" : "h-11 w-full px-4",
          on ? "bg-brand text-white" : "text-foreground hover:bg-secondary",
          chip && !on && "bg-secondary",
          gone && "cursor-not-allowed opacity-40",
        )}
      >
        {p.label}
      </button>
    );
  };

  return (
    <span ref={wrapRef} className={cn("relative", anchorClassName ?? "inline-flex")}>
      {anchor}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label={title ? `Select ${title.toLowerCase()}` : "Select date"}
              data-vz-popover=""
              style={pos ? { top: pos.top, left: pos.left } : undefined}
              className={cn(
                "pointer-events-auto fixed z-[60] max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden rounded-[28px] border bg-popover text-left text-popover-foreground shadow-md lg:w-[36rem] lg:overflow-hidden",
                pos ? "" : "inset-x-4 top-1/2 -translate-y-1/2 lg:invisible",
              )}
            >
              <div className="flex flex-col lg:flex-row">
                <div className="hidden w-44 shrink-0 flex-col gap-1 border-r border-border p-3 lg:flex">
                  {presets.map((p) => presetButton(p, false))}
                </div>
                <div className="flex gap-2 overflow-x-auto border-b border-border p-3 [scrollbar-width:none] lg:hidden">
                  {presets.map((p) => presetButton(p, true))}
                </div>
                <div className="min-w-0 flex-1 p-4">
                  <Month
                    month={month}
                    onMonthChange={setMonth}
                    // One date: the dropdowns only move the view.
                    onMonthPick={setMonth}
                    prev
                    next
                    from={draft}
                    to={draft}
                    hover={null}
                    onDay={pick}
                    onHover={() => undefined}
                    minDate={minDate}
                  />
                  {withTime ? (
                    <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
                      <span className="mr-auto text-sm font-semibold">Time</span>
                      <PillSelect
                        label="Hour"
                        value={hour}
                        onChange={(h) => {
                          setHour(h);
                          if (draft) bumpIfPast(draft, h, minute);
                        }}
                        options={hourOptions}
                        className="w-[5.5rem]"
                      />
                      <span className="text-sm font-semibold text-muted-foreground">:</span>
                      <PillSelect
                        label="Minute"
                        value={minute}
                        onChange={setMinute}
                        options={minuteOptions}
                        className="w-[5.5rem]"
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border px-4 py-3">
                <span className="w-full text-sm tabular-nums text-muted-foreground lg:mr-auto lg:w-auto">
                  {draftPast ? (
                    <span className="text-destructive">Pick a time in the future</span>
                  ) : draft ? (
                    format(
                      withClock(draft),
                      withTime ? "EEE d MMM yyyy · HH:mm" : "EEEE d MMMM yyyy",
                    )
                  ) : (
                    "Pick a day"
                  )}
                </span>
                {value && onClear ? (
                  <button
                    type="button"
                    onClick={() => {
                      onClear();
                      onOpenChange(false);
                    }}
                    className="h-10 rounded-full border border-brand px-6 text-sm font-semibold text-brand transition-colors hover:bg-brand/5"
                  >
                    Clear
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={!draft || draftPast}
                  onClick={() => {
                    if (!draft || draftPast) return;
                    onApply(withClock(draft));
                    onOpenChange(false);
                  }}
                  className="h-10 rounded-full bg-brand px-7 text-sm font-semibold text-white transition-colors hover:bg-brand/90 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
